import { useState } from 'react';
import type { ReactNode } from 'react';
import { useUrlState } from '../../lib/useUrlState';
import type { AccessEvent, MentionUser } from '../../lib/access';
import { RichEditor } from '../RichEditor';
import { ErrorBoundary } from '../ErrorBoundary';
import { FoldableMarkdown } from '../FoldableMarkdown';
import { Icon } from '../Icon';
import type { IconName } from '../Icon';
import { Avatar } from './AttributeSidebar';
import { formatDateTime } from '../../lib/format';

const EVENT_LABELS: Record<string, string> = {
  created: 'created the task',
  attached: 'uploaded a file',
  assigned: 'was assigned',
  assignee_added: 'assigned someone',
  assignee_removed: 'unassigned someone',
  // Legacy kinds from the removed layered review: neutral wording, no layer / sign-off jargon.
  escalated: 'asked another reviewer',
  maintainer_signoff_requested: 'asked for a review',
  maintainer_signoff: 'reviewed the task',
  commented: 'commented',
  decided: 'decided',
  cancelled: 'cancelled the task',
};

export type Tone = '' | 'ok' | 'warn' | 'acc';

/** Icon + tint for a system event's one-liner. */
function eventIcon(kind: string, meta: Record<string, unknown>): [IconName, Tone] {
  switch (kind) {
    case 'created':
      return ['access', 'acc'];
    case 'assigned':
    case 'assignee_added':
    case 'assignee_removed':
      return ['users', ''];
    case 'escalated':
    case 'maintainer_signoff_requested':
    case 'maintainer_signoff':
      return ['users', ''];
    case 'decided':
      // ⊗ only for a denial; approved = check; status unknown (legacy rows) = neutral check.
      return meta.status === 'denied' ? ['cross', 'warn'] : meta.status === 'approved' ? ['check', 'ok'] : ['check', ''];
    case 'cancelled':
      return ['close', 'warn'];
    case 'attached':
      return ['docs', ''];
    default:
      return ['clock', ''];
  }
}

function safeParse(s: string): Record<string, unknown> {
  try {
    return JSON.parse(s) ?? {};
  } catch {
    return {};
  }
}

export type ActivityShow = 'all' | 'comments' | 'history';

interface ActivityFeedProps {
  events: AccessEvent[];
  myEmail: string;
  busy: boolean;
  mentionUsers: MentionUser[];
  editingCommentId: string | null;
  commentDraft: string;
  setCommentDraft: (v: string) => void;
  onStartEdit: (e: AccessEvent) => void;
  onCancelEdit: () => void;
  onSaveEdit: (e: AccessEvent) => void;
  /** The API's refusal of the last comment edit, shown under that editor. */
  editError?: string | null;
  /** Omitted = comments cannot be deleted on this surface (no delete button). */
  onAskDelete?: (e: AccessEvent) => void;
  /** Root may delete anyone's comment (the API allows author or root); edit stays author-only. */
  canDeleteAny?: boolean;
  /** Event-kind labels for the task; falls back to the access-task labels above. */
  labels?: Record<string, string>;
  /** Icon + tint for another request type's system events; undefined → the access-request icon. */
  iconFor?: (kind: string, meta: Record<string, unknown>) => [IconName, Tone] | undefined;
  /** Replaces the label + meta detail of a system event; undefined → the access-request rendering. */
  describe?: (e: AccessEvent, meta: Record<string, unknown>) => ReactNode | undefined;
  /** The grant's status, for `decided` rows written without one in their meta (legacy / seed). */
  decidedStatus?: string | null;
}

/**
 * The request's audit trail. System events are compact one-liners with an icon;
 * comments are cards (own comments editable / deletable, as before). Filter and
 * order live in the URL (`?show=comments&order=newest`) so a view can be linked.
 */
export function ActivityFeed(props: ActivityFeedProps) {
  const { events, myEmail, busy, mentionUsers, editingCommentId, commentDraft, setCommentDraft } = props;
  const [showRaw, setShow] = useUrlState('show', 'all');
  const [orderRaw, setOrder] = useUrlState('order', 'oldest');
  // the comment being edited has an attachment still uploading → Save waits for it
  const [editUploading, setEditUploading] = useState(false);
  const show: ActivityShow = showRaw === 'comments' || showRaw === 'history' ? showRaw : 'all';
  const newest = orderRaw === 'newest';
  const labelOf = (kind: string) => props.labels?.[kind] ?? EVENT_LABELS[kind] ?? kind;

  const filtered = events.filter((e) =>
    show === 'all' ? true : show === 'comments' ? e.kind === 'commented' : e.kind !== 'commented',
  );
  const shown = newest ? [...filtered].reverse() : filtered;

  return (
    <section data-testid="req-activity">
      <div className="req-acthead req-acthead--section">
        <h3 className="section">Activity</h3>
        <div className="req-acttools">
          <div className="view-toggle" role="group" aria-label="Show" data-testid="req-activity-filter">
            {(
              [
                ['all', 'All'],
                ['comments', 'Comments'],
                ['history', 'History'],
              ] as const
            ).map(([k, label]) => (
              <button
                key={k}
                type="button"
                className={show === k ? 'active' : undefined}
                aria-pressed={show === k}
                onClick={() => setShow(k)}
              >
                {label}
              </button>
            ))}
          </div>
          <div className="view-toggle" role="group" aria-label="Order">
            <button
              type="button"
              className={!newest ? 'active' : undefined}
              aria-pressed={!newest}
              onClick={() => setOrder('oldest')}
            >
              Oldest first
            </button>
            <button
              type="button"
              className={newest ? 'active' : undefined}
              aria-pressed={newest}
              onClick={() => setOrder('newest')}
            >
              Newest first
            </button>
          </div>
        </div>
      </div>

      <ul className="req-act">
        {shown.length === 0 && (
          <li className="req-act__empty">{show === 'comments' ? 'No comments yet.' : 'Nothing to show.'}</li>
        )}
        {shown.map((e) => {
          const raw = safeParse(e.meta);
          const meta =
            e.kind === 'decided' && !raw.status && props.decidedStatus ? { ...raw, status: props.decidedStatus } : raw;
          const isComment = e.kind === 'commented';
          const canEditThis = isComment && e.actorEmail === myEmail && editingCommentId !== e.id;
          const canDeleteThis =
            isComment && (e.actorEmail === myEmail || !!props.canDeleteAny) && editingCommentId !== e.id;
          const time = <span className="tl-time">{formatDateTime(e.createdAt)}</span>;

          if (isComment) {
            return (
              <li key={e.id} className="req-ev req-ev--comment">
                <Avatar email={e.actorEmail} />
                <div className="tl-entry req-cmt">
                  <div className="tl-head">
                    <span className="tl-actor">{e.actorEmail}</span>
                    <span className="muted">{labelOf(e.kind)}</span>
                    {e.editedAt && <span className="muted">· edited</span>}
                    <span className="req-cmt__right">
                    {time}
                    {/* Icon-only, so the label the icon replaced moves to title + aria-label —
                        the contract Icon.tsx states, and the reason `title` alone is not
                        enough: it is a mouse-only affordance a screen reader may skip. */}
                    {canEditThis && (
                      <button
                        type="button"
                        className="req-icon-btn"
                        disabled={busy}
                        title="Edit this comment"
                        aria-label="Edit this comment"
                        onClick={() => props.onStartEdit(e)}
                      >
                        <Icon name="edit" />
                      </button>
                    )}
                    {canDeleteThis && props.onAskDelete && (
                      <button
                        type="button"
                        className="req-icon-btn req-icon-btn--danger"
                        disabled={busy}
                        title="Delete this comment"
                        aria-label="Delete this comment"
                        onClick={() => props.onAskDelete?.(e)}
                      >
                        <Icon name="trash" />
                      </button>
                    )}
                    </span>
                  </div>
                  {editingCommentId === e.id ? (
                    <div className="tl-body">
                      <ErrorBoundary fallback={(m) => <p className="error">editor failed: {m}</p>}>
                        <RichEditor
                          value={commentDraft}
                          onChange={setCommentDraft}
                          placeholder="edit comment…"
                          disabled={busy}
                          mentionUsers={mentionUsers}
                          autoFocus
                          onSubmit={() => !editUploading && props.onSaveEdit(e)}
                          onCancel={props.onCancelEdit}
                          onUploadingChange={setEditUploading}
                        />
                      </ErrorBoundary>
                      {props.editError && (
                        <p className="error cr-field-error" role="alert" data-testid="tl-edit-error">
                          {props.editError}
                        </p>
                      )}
                      <div className="btn-row" style={{ marginTop: 6 }}>
                        <button
                          className="btn btn-primary"
                          disabled={busy || editUploading || !commentDraft.trim()}
                          onClick={() => props.onSaveEdit(e)}
                        >
                          Save
                        </button>
                        <button className="btn btn-ghost" disabled={busy} onClick={props.onCancelEdit}>
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    e.note && (
                      <div className="tl-body">
                        <FoldableMarkdown>{e.note}</FoldableMarkdown>
                      </div>
                    )
                  )}
                </div>
              </li>
            );
          }

          const [icon, tone] = props.iconFor?.(e.kind, meta) ?? eventIcon(e.kind, meta);
          const custom = props.describe?.(e, meta);
          return (
            <li key={e.id} className="req-ev">
              <span className={`req-ev__ic${tone ? ` req-ev__ic--${tone}` : ''}`}>
                <Icon name={icon} size={13} />
              </span>
              <div className="req-ev__main">
                <div className="req-ev__line">
                  <span className="req-ev__txt">
                    <strong>{e.actorEmail}</strong> {custom !== undefined ? custom : <>{labelOf(e.kind)}
                    {e.kind === 'decided' && meta.status ? (
                      <>
                        {' '}
                        — <strong>{String(meta.status)}</strong>
                      </>
                    ) : null}
                    {e.kind === 'decided' && meta.selfDecided ? (
                      <>
                        {' '}
                        <span className="badge warn" title="Decided by the requester (root) — no second reviewer">
                          self-decided
                        </span>
                      </>
                    ) : null}
                    {Array.isArray(meta.assignees) && meta.assignees.length > 0 && (
                      <span className="muted"> · assigned: {meta.assignees.join(', ')}</span>
                    )}</>}
                    {e.editedAt && <span className="muted"> · edited</span>}
                  </span>
                  <span className="req-ev__time">{formatDateTime(e.createdAt)}</span>
                </div>
                {/* the 'created' note IS the description (shown above) — don't repeat it */}
                {e.note && e.kind !== 'created' && (
                  <div className="tl-body req-ev__note">
                    <FoldableMarkdown>{e.note}</FoldableMarkdown>
                  </div>
                )}
              </div>
            </li>
          );
        })}
      </ul>
    </section>
  );
}
